import type { Property, User } from '@kalos/kalos-rpc';
import {
  Button,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  toast,
} from '@kalos/ui';
import { useMutation } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { PropertyForm } from '../../../../components/PropertyForm';
import {
  createPropertyWithContact,
  type PropertyFormData,
} from '../../../../components/propertyFormUtils';
import { usePropertiesBatchGetQuery } from '../../../../hooks/react-query/usePropertiesQuery';

// -------------------- Types --------------------
export interface PropertyStepProps {
  customer: User;
  onSelectProperty: (property: Property) => void;
  onCreateProperty: (property: Property) => void;
  onBack: () => void;
  onChangeCustomer?: () => void;
  defaultTab?: 'select' | 'new';
  newJob?: boolean;
}

// -------------------- Component --------------------
export const PropertyStep = ({
  customer,
  onSelectProperty,
  onCreateProperty,
  onBack,
  onChangeCustomer,
  defaultTab = 'select',
  newJob = true,
}: PropertyStepProps) => {
  // -------------------- Tabs --------------------
  const [tab, setTab] = useState<'select' | 'new'>(defaultTab);

  // -------------------- Search State --------------------
  const [searchTerm, setSearchTerm] = useState('');

  // fetch all properties without limit
  const { data: propertiesData, isFetching: isPropertiesLoading } = usePropertiesBatchGetQuery({
    filter: { userId: customer.id, isActive: 1, withoutLimit: true },
    enabled: !!customer.id,
  });

  // Memoize properties data
  const properties = useMemo(() => propertiesData?.results ?? [], [propertiesData]);

  // Filter properties based on search term and limit to 25 results
  const filteredProperties = useMemo(() => {
    if (!searchTerm.trim()) {
      return properties.slice(0, 25);
    }

    const searchLower = searchTerm.toLowerCase();
    const filtered = properties.filter((property) => {
      const address = (property.address || '').toLowerCase();
      const city = (property.city || '').toLowerCase();
      const state = (property.state || '').toLowerCase();
      const zip = (property.zip || '').toLowerCase();
      const subdivision = (property.subdivision || '').toLowerCase();

      return (
        address.includes(searchLower) ||
        city.includes(searchLower) ||
        state.includes(searchLower) ||
        zip.includes(searchLower) ||
        subdivision.includes(searchLower)
      );
    });

    return filtered.slice(0, 25);
  }, [properties, searchTerm]);

  // mutation for create property
  const createPropertyMutation = useMutation({
    mutationFn: (data: PropertyFormData) => createPropertyWithContact(data, customer.id),
    onSuccess: (p) => {
      if (!p) return;
      toast({
        title: 'Property created',
        description: p.address,
        variant: 'success',
      });
      onCreateProperty(p);
    },
    onError: () => {
      toast({
        title: 'Failed to create property',
        description: 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  // -------------------- Render helpers --------------------
  const renderSelectTab = () => (
    <div className="space-y-4">
      {/* Search Input */}
      <div className="relative">
        <Input
          type="text"
          placeholder="Search properties by address, city, state, zip, or subdivision..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="h-10 pr-8"
        />
        {searchTerm && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1 h-8 w-8 p-0"
            onClick={() => setSearchTerm('')}
          >
            ×
          </Button>
        )}
      </div>

      {isPropertiesLoading ? (
        <p className="text-center text-gray-600">Loading properties…</p>
      ) : properties.length === 0 ? (
        <p className="text-center text-gray-600">This customer has no properties.</p>
      ) : (
        // relative contains the sr-only "Actions" head — see CustomerStep.
        <div className="relative max-h-[55vh] overflow-auto rounded-md border">
          {/* Results count */}
          <div className="border-b bg-gray-50 px-3 py-2 text-sm text-gray-600">
            {searchTerm ? (
              <>
                Showing {filteredProperties.length} of {properties.length} properties
                {filteredProperties.length === 25 &&
                  properties.length > 25 &&
                  ' (limited to 25 results)'}
              </>
            ) : (
              <>
                Showing {Math.min(properties.length, 25)} of {properties.length} properties
                {properties.length > 25 && ' (limited to 25 results)'}
              </>
            )}
          </div>

          {/* Properties List */}
          {filteredProperties.length === 0 ? (
            <p className="p-4 text-center text-gray-600">No properties match your search.</p>
          ) : (
            <Table className="min-w-[680px] table-fixed [&>tbody>tr>td]:px-3 [&>tbody>tr>td]:py-2 [&>thead>tr>th]:h-8 [&>thead>tr>th]:px-3 [&>thead>tr>th]:text-xs [&>thead>tr>th]:font-semibold [&>thead>tr>th]:uppercase [&>thead>tr>th]:tracking-wide [&>thead>tr>th]:text-gray-500">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[36%]">Address</TableHead>
                  <TableHead className="w-[26%]">City / State</TableHead>
                  <TableHead className="w-[24%]">Notes</TableHead>
                  <TableHead className="w-[100px]">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProperties.map((p) => {
                  const location = [p.city, p.state, p.zip].filter(Boolean).join(', ');
                  return (
                    <TableRow key={p.id}>
                      <TableCell className="font-semibold">
                        <span className="block truncate" title={p.address || undefined}>
                          {p.address || 'No address'}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-600">
                        <span className="block truncate" title={location || undefined}>
                          {location || 'No location details'}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-500">
                        <span className="block truncate" title={p.subdivision || undefined}>
                          {p.subdivision}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" onClick={() => onSelectProperty(p)}>
                          Select
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
      )}
    </div>
  );

  // -------------------- JSX --------------------
  return (
    <div className="flex w-full flex-col gap-6">
      {/* Customer Header */}
      <div className="flex items-center justify-between rounded-md border border-gray-200 bg-white p-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            {`${customer.firstname ?? ''} ${customer.lastname ?? ''}`.trim()}
          </h3>
          <p className="text-sm text-gray-600">Selected Customer</p>
        </div>
        {newJob && (
          <Button type="button" variant="outline" onClick={onBack}>
            Change Customer
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={(val) => setTab(val as typeof tab)} className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="select">Select Property</TabsTrigger>
          <TabsTrigger value="new">New Property</TabsTrigger>
        </TabsList>

        <TabsContent value="select" className="mt-4">
          {renderSelectTab()}
        </TabsContent>
        <TabsContent value="new" className="mt-4">
          <PropertyForm
            customer={customer}
            showContactSection
            onSubmit={(data) => createPropertyMutation.mutate(data)}
            submitting={createPropertyMutation.isPending}
            submitLabel="Add Property & Continue"
            footerStart={
              newJob ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onBack}
                  disabled={createPropertyMutation.isPending}
                >
                  Change Customer
                </Button>
              ) : undefined
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};
